/**
 * Dictionary — the phrases the user saved for later, kept purely locally in
 * IndexedDB. No backend contract, exactly like favorites.
 *
 * The `dictionary` store holds only `{ id, addedAt }`: everything the list shows
 * (content, translation, description, examples, audio) already lives in the
 * `phrases` row for that same id, so nothing is duplicated and a saved phrase can
 * never go stale against the cached content. `addedAt` is the one extra field,
 * and it exists for a single reason — the list is ordered newest-first, which
 * `{ id }` alone can't express.
 */

import { STORES, get, getAll, put, remove } from '../db/idb.js';
import { getPhrase } from './content.js';

/** Is this phrase in the dictionary? */
export async function isInDictionary(phraseId) {
  return Boolean(await get(STORES.dictionary, phraseId));
}

/** Save a phrase (idempotent — a re-add just refreshes its `addedAt`). */
export function addToDictionary(phraseId) {
  return put(STORES.dictionary, { id: phraseId, addedAt: Date.now() });
}

/** Drop a phrase from the dictionary (idempotent). */
export function removeFromDictionary(phraseId) {
  return remove(STORES.dictionary, phraseId);
}

/**
 * Flip a phrase's dictionary state and resolve with the **new** state, so callers
 * can drive their icon from what actually landed in the store rather than from a
 * local guess.
 */
export async function toggleDictionary(phraseId) {
  const next = !(await isInDictionary(phraseId));
  if (next) await addToDictionary(phraseId);
  else await removeFromDictionary(phraseId);
  return next;
}

/**
 * The saved phrases as full `phrases` rows (audio as playable Blobs), newest
 * addition first.
 *
 * A saved phrase whose `phrases` row is gone (its topic was updated and no longer
 * carries it, cache cleared) is skipped rather than deleted — the list just
 * doesn't show it.
 */
export async function getDictionaryPhrases() {
  const rows = await getAll(STORES.dictionary);
  const phrases = await Promise.all(
    rows.sort((a, b) => (b.addedAt ?? 0) - (a.addedAt ?? 0)).map((row) => getPhrase(row.id))
  );
  return phrases.filter(Boolean);
}
